import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, List, Search } from "lucide-react";
import { MonthCalendar } from "@/components/MonthCalendar";
import { EventCard } from "@/components/EventCard";
import { ShareDayButton } from "@/components/ShareDayButton";
import { BranchTabs } from "@/components/BranchTabs";
import { toast } from "sonner";
import { fetchEvents } from "@/lib/api";
import { BRANCHES, STATUS, toKey as keyOf } from "@/lib/format";

const FILTERS = [["all", "Todos"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])];

const Stat = ({ label, value, testId }) => (
  <div className="border-l border-slate-800 pl-4">
    <div data-testid={testId} className="font-display text-3xl font-bold">{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
  </div>
);

export default function AgendaPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState(new Date());
  const [params, setParams] = useSearchParams();
  const branch = BRANCHES[params.get("filial")] ? params.get("filial") : "all";
  const setBranch = (b) => setParams(b === "all" ? {} : { filial: b }, { replace: true });
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents().then(setEvents).catch(() => toast.error("Não foi possível carregar a agenda")).finally(() => setLoading(false));
  }, []);

  const scoped = useMemo(() => events.filter((e) => branch === "all" || e.branch === branch), [events, branch]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return scoped.filter((e) => (status === "all" || e.status === status) &&
      (!s || [e.title, e.location, e.client, e.address].join(" ").toLowerCase().includes(s)));
  }, [scoped, q, status]);

  const today = keyOf(new Date());
  const upcoming = filtered.filter((e) => e.teardown_date >= today);
  const past = filtered.filter((e) => e.teardown_date < today).reverse();
  const setupsToday = scoped.filter((e) => e.setup_date === today || e.teardown_date === today).length;

  return (
    <main className="relative z-10 mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-8">
      <section className="rise mb-10 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:gap-8">
          <img src="/logo.png" alt="Floor LED 360 - Aluguel ou Venda" data-testid="hero-logo" className="h-32 w-auto drop-shadow-[0_0_30px_rgba(225,6,0,0.35)] md:h-44" />
          <div>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400">Floor LED 360 · Agenda aberta</p>
          <h1 className="font-display text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl lg:text-6xl">Montagem, evento<br />e desmontagem.</h1>
          <p className="mt-4 max-w-xl text-sm text-slate-400 sm:text-base">Confira local, data, horários e equipamentos locados de cada evento.</p>
          </div>
        </div>
        <div className="flex gap-6">
          <Stat label="Eventos" value={scoped.length} testId="stat-total-events" />
          <Stat label="Próximos" value={scoped.filter((e) => e.teardown_date >= today).length} testId="stat-upcoming-events" />
          <Stat label="Mont./Desm. hoje" value={setupsToday} testId="stat-today" />
        </div>
      </section>

      <BranchTabs value={branch} onChange={setBranch} events={events} />

      <section className="mb-6 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex rounded-sm border border-slate-800 bg-slate-900/60 p-1">
          {[["calendar", "Calendário", CalendarDays], ["list", "Lista", List]].map(([k, label, Icon]) => (
            <button key={k} data-testid={`nav-${k}-tab`} onClick={() => setView(k)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-sm px-4 py-2 text-sm font-medium transition-colors ${view === k ? "bg-amber-500 text-white" : "text-slate-300 hover:text-white"}`}>
              <Icon size={15} />{label}
            </button>
          ))}
        </div>
        <ShareDayButton events={scoped} branch={branch} />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="search-event-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar evento, local ou cliente"
              className="h-10 w-full rounded-sm border border-slate-800 bg-slate-900/60 pl-9 pr-3 text-sm outline-none transition-colors placeholder:text-slate-500 focus:border-amber-500 sm:w-72 xl:w-56" />
          </div>
          <div className="flex flex-wrap gap-1 xl:shrink-0 xl:flex-nowrap">
            {FILTERS.map(([k, label]) => (
              <button key={k} data-testid={`status-filter-${k}`} onClick={() => setStatus(k)}
                className={`rounded-sm border px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${status === k ? "border-amber-500 text-amber-400" : "border-slate-800 text-slate-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div data-testid="agenda-loading" className="h-96 animate-pulse rounded-md bg-slate-900/60" />
      ) : view === "calendar" ? (
        <MonthCalendar month={month} setMonth={setMonth} events={filtered} onSelect={(ev) => navigate(`/evento/${ev.id}`)} />
      ) : (
        <div data-testid="event-list" className="space-y-10">
          <div className="space-y-3">
            <h2 className="font-mono text-xs uppercase tracking-widest text-slate-500">Próximos e em andamento ({upcoming.length})</h2>
            {upcoming.length === 0 && <p data-testid="event-list-empty" className="rounded-md border border-dashed border-slate-800 p-8 text-center text-slate-500">Nenhum evento encontrado.</p>}
            {upcoming.map((ev, i) => <EventCard key={ev.id} ev={ev} i={i} />)}
          </div>
          {past.length > 0 && (
            <div className="space-y-3 opacity-70">
              <h2 className="font-mono text-xs uppercase tracking-widest text-slate-500">Encerrados ({past.length})</h2>
              {past.map((ev, i) => <EventCard key={ev.id} ev={ev} i={i} />)}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
